import { capitalizeFirstLetter } from '@/helpers/StringHelpers';
import UserService from '@/services/users/UserService';

const getDefaultState = () => {
    return {
        user: {
            hasFetched: false,
            data: null,
        },
        rescheduledDeliveryDays: {
            data: [],
        },
        expandFormName: null,
        hasAnsweredCountrySwitch: localStorage.getItem('country-switch-answered'),
        signupCoupon: null,
        skipConsecutiveHolidayEnabled: false,
        outstandingOrders: [],
        setupIntent: null,
        fetchingSetupIntent: false,
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },

        countrySwitchHasAnswered: (state, bool) => {
            state.hasAnsweredCountrySwitch = bool;
            localStorage.setItem('country-switch-answered', bool);
        },

        updateUserData(state, payload) {
            state.user.data = payload;
        },

        updateExpandFormName(state, payload) {
            state.expandFormName = payload;
        },

        updateRescheduledDeliveryDays(state, payload) {
            state.rescheduledDeliveryDays.data = payload;
        },

        updateOutstandingOrders(state, payload) {
            state.outstandingOrders = payload;
        },

        setSignupCoupon(state, payload) {
            state.signupCoupon = payload;
        },

        setSkipConsecutiveHolidayEnabled: (state, payload) => state.skipConsecutiveHolidayEnabled = payload,

        setSetupIntent: (state, payload) => state.setupIntent = payload,

        setFetchingSetupIntent: (state, payload) => state.fetchingSetupIntent = payload,
    },
    actions: {
        fetchUserData({ commit, state, rootGetters, dispatch }, forceReload = false) {
            if (state.user.hasFetched === false || forceReload === true) {
                state.user.hasFetched = true;
                let service = new UserService();

                return new Promise((resolve, reject) => {
                    service.fetchUserData(rootGetters['auth/isCanvasser'])
                        .then(response => {
                            commit('updateUserData', response.data.data);
                            commit('setSkipConsecutiveHolidayEnabled', response.data?.data?.change_plan);
                            process.env.VUE_APP_COUNTRY_ISO === 'USA' && dispatch('cookies/mountStripe', {}, { root: true });
                            resolve(response);
                        })
                        .catch(error => {
                            state.user.hasFetched = false;
                            reject(error);
                        });
                });
            }
        },

        fetchOutstandingOrders({ commit, state, rootGetters }) {
            let service = new UserService();

            return new Promise((resolve, reject) => {
                service.getOutstandingOrders(rootGetters['account/getUserId'])
                    .then(response => {
                        commit('updateOutstandingOrders', response.data);
                    })
                    .catch(error => reject(error));
            });
        },

        payOutstandingOrders({ commit, state, rootGetters }, payload) {
            let service = new UserService();

            return new Promise((resolve, reject) => {
                service.payOutstandingOrders(rootGetters['account/getUserId'], payload)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => reject(error));
            });
        },

        resetAccount({ commit }) {
            commit('resetState');
        },

        async fetchStripeSetupIntent({ commit }, payload) {
            let service = new UserService();
            commit('setFetchingSetupIntent', true);
            let setupIntent = await service.fetchStripeSetupIntent(payload);
            commit('setFetchingSetupIntent', false);
            commit('setSetupIntent', setupIntent.data.intent_client_secret);
        },
    },
    getters: {
        getExpandFormName: (state) => state.expandFormName,

        getFirstName: (state) => state.user.hasFetched ? state.user.data.first_name : null,

        getLastName: (state) => state.user.hasFetched ? state.user.data.last_name : null,

        getEmail: (state) => state.user.hasFetched ? state.user.data.email : null,

        getFullName: (state, getters) => {
            return state.user.hasFetched ? capitalizeFirstLetter(`${getters.getFirstName} ${getters.getLastName}`) : null;
        },

        getUser: state => state.user.hasFetched && state.user.data ? state.user.data : null,

        getOutstandingOrders: state => state.outstandingOrders,

        getUserProperty: (state) => (property = null) => {
            return state.user.hasFetched ? state.user.data[property] || state.user.data : null;
        },

        getUserId: state => state.user.hasFetched && state.user.data ? state.user.data.id : null,

        getAllowPaused: state => state.user.hasFetched ? state.user.data.allow_paused : false,

        getUserFriendCode: state => state.user.hasFetched ? state.user.data.friend_code : false,

        getUserReferralsRemaining: state => state.user.hasFetched ? state.user.data.referrals_remaining : null,

        getUserInvitesRemaining: state => state.user.hasFetched ? state.user.data.invites_remaining : null,

        getRescheduledDeliveryDays: state => state.rescheduledDeliveryDays.data,

        getSkipConsecutiveHolidayEnabled: state => state.skipConsecutiveHolidayEnabled,

        getHasAnsweredCountrySwitch: state => state.hasAnsweredCountrySwitch,

        getCoupons: state => state.user.hasFetched && state.user.data.coupons,

        getSelfHostedUrl: state => state.user.hasFetched ? state.user.data.selfHostedUrl : null,

        getSignupCoupon: state => state.signupCoupon,

        getIsLilyFree: state => state.user.data.dislike_lilies,

        enableShop: state => (process.env.VUE_APP_SHOP_ENABLED === 'true') || (state.user.hasFetched && state.user.data.allow_shop),

        getShopInGiftingEnabled: state => state.user.data.shop_in_gifting,

        getGiftingVaseEnabled: state => process.env.VUE_APP_MIX_GIFTING_ENABLE_VASE === 'true' && !state.user.data.shop_in_gifting,

        getSetupIntent: state => state.setupIntent,

        isfetchingSetupIntent: state => state.fetchingSetupIntent,
    },
};
